import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Sun, Clock, DollarSign, Cake, AlertTriangle, CreditCard, Phone, Mail,
  User, Sparkles, Printer, RefreshCw, ChevronRight, Tag, FileText,
  Calendar as CalendarIcon
} from "lucide-react";

const TimeBlock = ({ startTime, endTime }) => {
  const formatTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    } catch { return ""; }
  };
  return (
    <div className="text-center min-w-[70px]">
      <p className="font-['Outfit'] text-sm font-semibold text-slate-900">{formatTime(startTime)}</p>
      <p className="text-[10px] text-slate-400">{formatTime(endTime)}</p>
    </div>
  );
};

export default function ShiftBriefingPage() {
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [briefingData, setBriefingData] = useState(null);
  const [aiSummary, setAiSummary] = useState("");
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [loadingAi, setLoadingAi] = useState(false);

  useEffect(() => {
    api.get("/staff/all").then(res => {
      const provs = (res.data || []).filter(u => ["stylist", "esthetician", "owner"].includes(u.role) && u.is_active);
      setProviders(provs);
      if (provs.length > 0) setSelectedProvider(provs[0].user_id);
    }).catch(() => toast.error("Failed to load providers"));
  }, []);

  const fetchBriefing = async () => {
    if (!selectedProvider) return;
    setLoadingBrief(true);
    setAiSummary("");
    try {
      const res = await api.get(`/shift-briefing?provider_id=${selectedProvider}&date=${date}`);
      setBriefingData(res.data?.briefings?.[0] || null);
    } catch {
      toast.error("Failed to load briefing");
    }
    setLoadingBrief(false);
  };

  useEffect(() => {
    if (selectedProvider) fetchBriefing();
  }, [selectedProvider, date]);

  const fetchAiSummary = async () => {
    if (!selectedProvider) return;
    setLoadingAi(true);
    try {
      const res = await api.get(`/shift-briefing/ai-summary?provider_id=${selectedProvider}&date=${date}`);
      setAiSummary(res.data?.summary || "");
    } catch {
      toast.error("Failed to generate AI summary");
    }
    setLoadingAi(false);
  };

  const handlePrint = () => window.print();

  const providerInfo = providers.find(p => p.user_id === selectedProvider);
  const appts = briefingData?.appointments || [];
  const stats = briefingData?.stats || {};

  const formatDate = (d) => {
    try {
      return new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    } catch { return d; }
  };

  return (
    <div data-testid="shift-briefing-page" className="p-6 lg:p-8 max-w-[1200px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <Sun className="w-6 h-6 text-[#F59E0B]" /> Daily Shift Briefing
          </h1>
          <p className="text-sm text-slate-400 mt-1">AI-powered morning prep for every stylist</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={handlePrint} className="border-slate-200 text-slate-600" data-testid="print-briefing-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 print:hidden">
        <div className="w-full sm:w-[240px]">
          <label className="text-xs text-slate-400 mb-1 block">Provider</label>
          <Select value={selectedProvider} onValueChange={setSelectedProvider}>
            <SelectTrigger className="border-slate-200 rounded-lg" data-testid="provider-select">
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent>
              {providers.map(p => (
                <SelectItem key={p.user_id} value={p.user_id}>
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color || "#1A1A1A" }} />
                    {p.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-[180px]">
          <label className="text-xs text-slate-400 mb-1 block">Date</label>
          <Input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="border-slate-200"
            data-testid="date-input"
          />
        </div>
        <div className="flex items-end">
          <Button size="sm" onClick={fetchAiSummary} disabled={loadingAi || !selectedProvider} className="bg-gradient-to-r from-[#1A1A1A] to-[#333] text-white h-10 px-4" data-testid="ai-summary-btn">
            <Sparkles className="w-4 h-4 mr-1.5" /> {loadingAi ? "Generating..." : "AI Brief"}
          </Button>
        </div>
      </div>

      {loadingBrief ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-5 h-5 animate-spin text-rose-400" />
        </div>
      ) : (
        <>
          {/* Provider Header Card */}
          {providerInfo && (
            <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-['Outfit'] font-semibold text-lg" style={{ backgroundColor: providerInfo.color || "#1A1A1A" }}>
                  {providerInfo.name?.charAt(0)}
                </div>
                <div>
                  <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">{providerInfo.name}</h2>
                  <p className="text-sm text-slate-400">{providerInfo.title || providerInfo.role} &middot; {formatDate(date)}</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="text-center" data-testid="stat-appt-count">
                  <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{stats.total_appointments || 0}</p>
                  <p className="text-xs text-slate-400">Appointments</p>
                </div>
                <div className="text-center" data-testid="stat-est-revenue">
                  <p className="font-['Outfit'] text-xl font-semibold text-emerald-600">${stats.estimated_revenue || 0}</p>
                  <p className="text-xs text-slate-400">Est. Revenue</p>
                </div>
                <div className="text-center" data-testid="stat-booked-hours">
                  <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{stats.booked_hours || 0}h</p>
                  <p className="text-xs text-slate-400">Booked</p>
                </div>
              </div>
            </div>
          )}

          {/* AI Summary */}
          {aiSummary && (
            <div data-testid="ai-summary-card" className="bg-gradient-to-br from-[#FAFAF9] to-[#F0EFED] border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-rose-400" />
                <h3 className="font-['Outfit'] font-medium text-slate-900 text-sm">AI Morning Brief</h3>
              </div>
              <p className="text-sm text-[#3D3D3D] leading-relaxed whitespace-pre-line">{aiSummary}</p>
            </div>
          )}

          {/* Appointment Timeline */}
          {appts.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
              <CalendarIcon className="w-10 h-10 mx-auto mb-3 text-[#D1D1D1]" />
              <p className="text-slate-400 font-medium">No appointments scheduled for this day</p>
              <p className="text-xs text-rose-400 mt-1">Great time for walk-ins or training sessions</p>
            </div>
          ) : (
            <div className="space-y-3">
              <h3 className="font-['Outfit'] font-medium text-slate-900 text-sm uppercase tracking-wider">Schedule ({appts.length} appointments)</h3>
              {appts.map((appt, i) => (
                <div
                  key={appt.appointment_id}
                  data-testid={`briefing-card-${i}`}
                  className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex gap-4">
                    {/* Time column */}
                    <TimeBlock startTime={appt.start_time} endTime={appt.end_time} />

                    {/* Divider */}
                    <div className="w-px bg-[#E6E4E0] self-stretch" />

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-['Outfit'] font-semibold text-slate-900">{appt.client_name}</h4>
                            {appt.birthday_alert && (
                              <span data-testid={`birthday-${i}`} className="inline-flex items-center gap-1 text-xs bg-[#FEF3CD] text-[#856404] px-2 py-0.5 rounded-full">
                                <Cake className="w-3 h-3" /> Birthday!
                              </span>
                            )}
                            {appt.noshow_count > 0 && (
                              <span data-testid={`noshow-warn-${i}`} className="inline-flex items-center gap-1 text-xs bg-[#FDE8E8] text-rose-600 px-2 py-0.5 rounded-full">
                                <AlertTriangle className="w-3 h-3" /> {appt.noshow_count} no-shows
                              </span>
                            )}
                            {appt.deposit_required && (
                              <span className="inline-flex items-center gap-1 text-xs bg-[#E0E7FF] text-[#3730A3] px-2 py-0.5 rounded-full">
                                <CreditCard className="w-3 h-3" /> Deposit
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                            {appt.client_phone && (
                              <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {appt.client_phone}</span>
                            )}
                            {appt.client_email && (
                              <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {appt.client_email}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-['Outfit'] font-semibold text-emerald-600">${appt.total_price}</p>
                          <p className={`text-xs mt-0.5 ${appt.status === "no-show" ? "text-rose-600" : "text-slate-400"}`}>{appt.status}</p>
                        </div>
                      </div>

                      {/* Services */}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {appt.service_names.map((svc, si) => (
                          <span key={`${appt.appointment_id}-svc-${si}`} className="text-xs bg-[#FAFAFA] text-slate-600 px-2.5 py-1 rounded-md border border-slate-200">{svc}</span>
                        ))}
                      </div>

                      {/* Tags & Notes */}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {appt.client_tags?.length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap">
                            <Tag className="w-3 h-3 text-rose-400" />
                            {appt.client_tags.map((tag, ti) => (
                              <span key={`${appt.appointment_id}-tag-${ti}`} className="text-[10px] text-rose-400 bg-[#FAFAFA] px-1.5 py-0.5 rounded">{tag}</span>
                            ))}
                          </div>
                        )}
                        {appt.card_on_file && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-[#155724] bg-[#D4EDDA] px-1.5 py-0.5 rounded">
                            <CreditCard className="w-2.5 h-2.5" /> Card on file
                          </span>
                        )}
                      </div>
                      {appt.client_notes && (
                        <div className="mt-2 flex items-start gap-1.5 text-xs text-slate-600 bg-[#FAFAF9] rounded-md p-2 border border-slate-100">
                          <FileText className="w-3 h-3 mt-0.5 flex-shrink-0 text-rose-400" />
                          <span>{appt.client_notes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
